'use client'

import { Badge, Cell, Image, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { HorizontalScrollArea } from '@marketplace-web/common-components/horizontal-scroll-area-ui'
import {
  ComplaintOrderItemModel,
  orderDetailsItemImageClickEvent,
  TransactionItemModel,
  TransactionSide,
} from '@marketplace-web/order-details/order-details-image-data'

type Props = {
  orderItems: Array<TransactionItemModel>
  complaintOrderItems: Array<ComplaintOrderItemModel> | null | undefined
  userSide: TransactionSide | null
  transactionId: number | null | undefined
  onItemClick: () => void
}

const OrderItems = ({
  orderItems,
  complaintOrderItems,
  userSide,
  transactionId,
  onItemClick,
}: Props) => {
  const translate = useTranslate('a11y')
  const { track } = useTracking()

  const isBundle = orderItems.length > 1

  const trackItemClick = (item: TransactionItemModel, index: number) => {
    if (!transactionId || !userSide) return

    track(
      orderDetailsItemImageClickEvent({
        transactionId: transactionId.toString(),
        userSide,
        itemId: item.id.toString(),
        positionInList: index + 1,
        itemsInList: orderItems.length,
      }),
    )
  }

  const handleItemClick = (item: TransactionItemModel, index: number) => {
    trackItemClick(item, index)
    onItemClick()
  }

  const renderOrderItemDetails = (item: TransactionItemModel) => {
    const complaintOrderItem = complaintOrderItems?.find(
      complaintItem => complaintItem.item.id === item.id,
    )

    return (
      <>
        <Text text={item.title} as="span" type={isBundle ? undefined : 'title'} width="parent" />
        {complaintOrderItem?.reasonText && (
          <Badge content={complaintOrderItem.reasonText} theme="warning" styling="light" />
        )}
      </>
    )
  }

  if (isBundle) {
    return (
      <Cell
        body={
          <HorizontalScrollArea
            showControls
            controlsScrollType={HorizontalScrollArea.ControlScrollType.Partial}
            arrowLeftText={translate('actions.move_left')}
            arrowRightText={translate('actions.move_right')}
          >
            {orderItems.map((item, index) => {
              const { thumbnailUrl, id, url } = item

              return (
                <HorizontalScrollArea.Item
                  key={id}
                  className="order-details__bundle-item-container"
                >
                  <Cell
                    styling="tight"
                    url={url}
                    onClick={() => handleItemClick(item, index)}
                    clickable
                    body={
                      <Image
                        src={thumbnailUrl}
                        size="x3-large"
                        ratio="small-portrait"
                        styling="rounded"
                      />
                    }
                  />
                  <Spacer />
                  {renderOrderItemDetails(item)}
                </HorizontalScrollArea.Item>
              )
            })}
          </HorizontalScrollArea>
        }
        divider
      />
    )
  }

  const item = orderItems[0]

  if (!item) return null

  return (
    <Cell
      prefix={
        <Image src={item?.thumbnailUrl} size="x3-large" ratio="small-portrait" styling="rounded" />
      }
      body={
        <div className="order-details__single-item-container">{renderOrderItemDetails(item)}</div>
      }
      url={item.url}
      onClick={() => handleItemClick(item, 0)}
      type="navigating"
      divider
    />
  )
}

export default OrderItems
